// @flow
import React from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash';
import useSheet from 'app/jss';
import t from 'app/utils/translate';
import { FRIEND_REQUESTS } from 'app/constants/friendStatusTypes';
import * as FriendActions from 'app/actions/friendsActions';
import interactableFriendGroupsSelector from 'app/selectors/friends/interactableFriendGroupsSelector';
import FriendIdentity from 'app/components/friends/common/FriendIdentity';
import KebabMenu from 'app/components/commons/KebabMenu';
import KebabMenuOption from 'app/components/commons/KebabMenuOption';
import FriendConfirmBox from 'app/components/friends/common/FriendConfirmBox';
import FriendDetailsHeader from './FriendDetailsHeader';
import FriendDetailsPanel from './panels/FriendDetailsPanel';

type Props = {
  friend: Friend,
  sheet: Object,
  onClick?: (id: number) => void,
  // provided by connect
  friendGroups?: Array<FriendGroup>,
  onRemoveFriend: (friendId: number) => Promise<Object>,
};

type State = {
  activeOption: Object,
  activeTab: any,
  deleting: boolean,
  isRemoving: boolean,
  rejecting: boolean,
};

const OptionTypes = [{ name: 'group', icon: 'group', title: t('groups') }];

class FriendDetails extends React.Component {
  props: Props;
  state: State = {
    isRemoving: false,
    rejecting: false,
    deleting: false,
    activeOption: OptionTypes[0],
    activeTab: undefined,
  };

  static defaultProps = {
    onCreateGroup: noop,
    onClick: noop,
    friendGroups: [],
  };

  onRemoveFriend = () => {
    // remove the friend
    this.setState({ isRemoving: true });
  };

  onConfirmRemove = () => {
    const { onRemoveFriend, friend } = this.props;
    onRemoveFriend(friend.id);
  };

  onCancelRemove = () => {
    this.setState({ isRemoving: false });
  };

  onOptionClick = (option) => {
    this.setState({ activeOption: option });
  };

  render() {
    const { sheet: { classes }, onClick, friend } = this.props;

    const { isRemoving } = this.state;

    return (
      <div className={classes.friendDetails}>
        <FriendDetailsHeader onClick={onClick}>
          <div className={classes.friendActions}>
            <div className={classes.actionButtonContainer}>
              <KebabMenu className={classes.menu}>
                <KebabMenuOption icon="icon-remove-friend" onClick={this.onRemoveFriend}>
                  <span>{t('Remove')}</span>
                </KebabMenuOption>
              </KebabMenu>
            </div>
          </div>
          <FriendIdentity
            friend={friend}
            subtitle={isRemoving ? t('Confirm to remove friend') : undefined}
            errored={isRemoving}
          />
          {isRemoving && (
            <FriendDetailsPanel isOtherState={isRemoving}>
              <FriendConfirmBox onConfirm={this.onConfirmRemove} onCancel={this.onCancelRemove} />
            </FriendDetailsPanel>
          )}
        </FriendDetailsHeader>
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    menu: {
      fontSize: theme.fontSize.large,
      [theme.mediaQuery.desktopAndUp]: {
        fontSize: theme.fontSize.medium,
      },
    },
    friendDetails: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontSmoothing: 'antialiased',
    },
    friendActions: {
      display: 'flex',
      width: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      padding: 10,
    },
    // action button must be aligned to the right
    // when the back button isn't there, we can't use space-between
    // so instead, always make the container full width and align to end
    actionButtonContainer: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      height: '2rem',
    },
  };
}

function mapStateToProps(state) {
  return {
    friendGroups: interactableFriendGroupsSelector(state),
  };
}

const actions = {
  onRemoveFriend: FriendActions.removeFriend,
};

export default connect(mapStateToProps, actions)(useSheet(applyStyles)(FriendDetails));
